import { useState, useEffect, useCallback, useRef } from "react";
import { Navigation, LocateFixed, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Show real precision so people know exactly how close they are: one decimal
// under 10 miles (e.g. "0.6"), whole numbers beyond that (e.g. "24").
const fmtMiles = (m) => {
    if (m == null) return null;
    if (m < 10) return (Math.round(m * 10) / 10).toString();
    return Math.round(m).toString();
};

/**
 * Inline distance control for a detail page: "{miles} mi from [zip input] [locate]".
 * The user can type a ZIP or tap the crosshair to use their precise device location.
 * onZip(zip, coords) fires so the parent can recompute distance (using coords when
 * present for GPS-accurate distance) AND persist the ZIP to the shared search filters.
 */
export default function DistanceZip({ distanceMiles, zip, onZip, accent = "#ff4500", testid = "detail-distance" }) {
    const [val, setVal] = useState(zip || "");
    const [locating, setLocating] = useState(false);
    const [precise, setPrecise] = useState(false);  // true once the device's precise location is in use

    // Keep latest props/state readable inside stable callbacks without recreating them
    // (recreating runLocate would re-fire the auto-locate effect in a loop).
    const onZipRef = useRef(onZip);
    const valRef = useRef(val);
    const zipRef = useRef(zip);
    useEffect(() => { onZipRef.current = onZip; valRef.current = val; zipRef.current = zip; });

    useEffect(() => { setVal(zip || ""); }, [zip]);

    const commit = (raw) => {
        const c = raw.replace(/\D/g, "").slice(0, 5);
        setVal(c);
        setPrecise(false);  // typing a ZIP switches back to ZIP-centroid distance
        // Manual ZIP entry clears any precise device coords (use the ZIP centroid).
        if (c.length === 5 || c.length === 0) onZip(c, null);
    };

    // Apply the device's precise coordinates. `silent` skips toasts/loading UI for the
    // automatic on-load use when location permission was already granted.
    const runLocate = useCallback((silent = false) => {
        if (!navigator.geolocation) {
            if (!silent) toast.error("Location not supported on this device");
            return;
        }
        if (!silent) setLocating(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setPrecise(true);
                // Use the precise coords for distance right away — independent of whether the
                // reverse-ZIP lookup succeeds — so accuracy never depends on that network call.
                onZipRef.current(valRef.current || zipRef.current || "", coords);
                try {
                    const { data } = await api.get("/geo/reverse-zip", { params: coords });
                    if (data?.zip) {
                        setVal(data.zip);
                        onZipRef.current(data.zip, coords);
                        if (!silent) toast.success("Using your precise location");
                    } else if (!silent) {
                        toast.success("Using your precise location");
                    }
                } catch {
                    if (!silent) toast.success("Using your precise location");
                } finally {
                    setLocating(false);
                }
            },
            () => { setLocating(false); if (!silent) toast.error("Location permission denied"); },
            { timeout: 8000, enableHighAccuracy: true }
        );
    }, []);

    // On load, if the user has already granted location permission, use their precise
    // device position automatically so the distance reflects true proximity — no tap needed.
    const didAuto = useRef(false);
    useEffect(() => {
        if (didAuto.current || !navigator.permissions?.query) return;
        didAuto.current = true;
        navigator.permissions.query({ name: "geolocation" })
            .then((res) => { if (res.state === "granted") runLocate(true); })
            .catch(() => {});
    }, [runLocate]);

    const showDist = (!!zip || precise) && distanceMiles != null;
    // Auto-size the ZIP box tight to its content: just enough for the "enter zip code"
    // placeholder when empty, and tight to the typed ZIP once entered — no trailing space.
    const inputWidth = val ? `calc(${val.length}ch + 1.1rem)` : "calc(10.5ch + 1.1rem)";

    return (
        <span data-testid={testid} className="inline-flex items-center gap-1.5 text-sm">
            <Navigation className="h-4 w-4 shrink-0" style={{ color: accent }} />
            {showDist
                ? <span className="font-medium" style={{ color: accent }}>{fmtMiles(distanceMiles)}</span>
                : <span className="text-[#a0a0a0] font-medium" data-testid={`${testid}-placeholder`}>XX</span>}
            <span className={showDist ? "font-medium" : "text-[#a0a0a0]"} style={showDist ? { color: accent } : undefined}>mi from</span>
            {precise ? (
                <button
                    type="button"
                    data-testid={`${testid}-zip`}
                    onClick={() => { setPrecise(false); setVal(""); onZip("", null); }}
                    title="Using your device location — tap to enter a ZIP instead"
                    className="h-7 bg-[#141414] border border-[#2a2a2a] hover:border-[#ff4500] rounded-none px-2 text-sm font-medium transition-colors"
                    style={{ color: accent }}
                >
                    your location
                </button>
            ) : (
                <input
                    data-testid={`${testid}-zip`}
                    value={val}
                    onChange={(e) => commit(e.target.value)}
                    inputMode="numeric"
                    pattern="[0-9]{5}"
                    placeholder="enter zip code"
                    style={{ width: inputWidth, color: val ? accent : undefined }}
                    className="h-7 bg-[#141414] border border-[#2a2a2a] focus:border-[#ff4500] rounded-none px-2 text-sm outline-none placeholder:text-[#666]"
                />
            )}
            <button
                type="button"
                data-testid={`${testid}-locate`}
                onClick={() => runLocate(false)}
                title="Use my precise location"
                aria-label="Use my precise location"
                className="h-7 w-7 flex items-center justify-center border border-[#2a2a2a] hover:border-[#ff4500] transition-colors shrink-0"
                style={{ color: accent }}
            >
                {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />}
            </button>
        </span>
    );
}
